/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */
import * as React from 'react';

import { PrismicPreviewProvider, componentResolverFromMap } from 'gatsby-plugin-prismic-previews';

import Page from './src/templates/page.tsx';
import Legal from './src/templates/legal.tsx';
import Contact from './src/pages/contact.tsx';
import BlogIndex from './src/pages/blog.tsx';
import BlogPost from './src/templates/blog-post.tsx';
import GuidesIndex from './src/pages/guides.tsx';
import GuidesPost from './src/templates/guides-post.tsx';
import CaseStudiesIndex from './src/pages/case-studies.tsx';
import CaseStudiesPost from './src/templates/case-studies-post.tsx';

import '@styles/typography.css';
const { linkResolver } = require('./src/helpers/linkResolve');

/**
 * UTM Attribution Tracking
 * --------------------------
 * Captures utm_* parameters from the URL on every page load and persists them
 * to sessionStorage. When a HubSpot form becomes ready (hsFormCallback /
 * onFormReady), the saved values are injected into the corresponding hidden
 * input fields so that form submissions carry full UTM attribution data.
 */
export const onClientEntry = () => {
  const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

  // 1. Parse the URL and save any found UTM values to sessionStorage
  const urlParams = new URLSearchParams(window.location.search);
  utmParams.forEach((param) => {
    if (urlParams.has(param)) {
      sessionStorage.setItem(param, urlParams.get(param));
    }
  });

  // 2. Inject saved UTM values into HubSpot hidden form fields when form loads
  window.addEventListener('message', (event) => {
    if (
      event.data &&
      event.data.type === 'hsFormCallback' &&
      event.data.eventName === 'onFormReady'
    ) {
      utmParams.forEach((param) => {
        const savedValue = sessionStorage.getItem(param);
        if (savedValue) {
          const input = document.querySelector(`input[name="${param}"]`);
          if (input) {
            input.value = savedValue;
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }
      });
    }
  });
};

export const onRouteUpdate = ({ location, prevLocation }) => {
  if (!prevLocation && !location.hash) {
    // do not restore scroll position on page refresh without hash
    setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'instant' }), 0);
  }
};

export const shouldUpdateScroll = ({ routerProps: { location }, getSavedScrollPosition }) => {
  window.history.scrollRestoration = 'manual';
  const currentPosition = getSavedScrollPosition(location) || [0, 0];

  setTimeout(() => {
    if (location.hash) {
      // If hash (`/#something`), smooth scroll to that position
      const item = document.querySelector(`${location.hash}`);
      item?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // when using the browser back/forward buttons or navigating to a different page client-side, instant scroll to its last saved position
      window.scrollTo({
        top: currentPosition[1],
        left: currentPosition[0],
        behavior: 'instant',
      });
    }
  }, 0);

  // do not let gatsby do anything more
  return false;
};

export const wrapRootElement = ({ element }) => (
  <PrismicPreviewProvider
    repositoryConfigs={[
      {
        repositoryName: process.env.GATSBY_PRISMIC_REPO_NAME,
        linkResolver,
        componentResolver: componentResolverFromMap({
          page: Page,
          legal: Legal,
          contact: Contact,
          blog_post: BlogPost,
          blog_index: BlogIndex,
          guides_post: GuidesPost,
          guides_index: GuidesIndex,
          case_studies_post: CaseStudiesPost,
          case_studies_index: CaseStudiesIndex,
        }),
      },
    ]}
  >
    {element}
  </PrismicPreviewProvider>
);
